import { Box } from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import DataGridComponent from "../../components/datagrid/datagrid.component";
import { toolsTableColumns } from "../../data/toolsTableConf";
import { useNavigate } from "react-router";
import { getRouterPaths } from "../../router/routerPaths";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import CustomDialog from "../../components/muiDialogs/dialog.component";
import { dialogMessages } from "../../components/forms/utils";
import {
  bulkDeleteInstrumentsRequest,
  fetchInstrumentsStart,
  resetInstrumentsStatus,
} from "../../store/features/customer/instruments/instrumentsSlice";
import { fetchSensorsStart } from "../../store/features/customer/instruments/sensorsSlice";
import {
  deleteSensorStart,
  resetDeleteSensorState,
} from "../../store/features/customer/sensor/sensorSlice";
import {
  deleteGatewayStart,
  resetDeleteGatewayState,
} from "../../store/features/customer/gateway/gatewaySlice";
import CustomAlert from "../../components/alert/alert.component";
import EmptyState from "../../components/emptyStates/emptyState.component";
import { getErrorMessage } from "../../utils/errorUtils";

const ToolsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const searchTimeoutRef = useRef();

  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkDeleteDialog, setOpenBulkDeleteDialog] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const type = useSelector((state) => state?.me?.data?.data?.type);
  const instruments = useSelector((state) => state.instruments?.data?.data);
  const success = useSelector((state) => state.sensor?.success);
  const sensorError = useSelector((state) => state.sensor?.error);
  const { isLoading } = useSelector((state) => state.instruments);
  const gatewaySuccess = useSelector((state) => state.gateway?.success);
  const gatewayError = useSelector((state) => state.gateway?.error);
  const accessToken = useSelector((state) => state.auth?.user?.access_token);
  const bulkDeleteSuccess = useSelector((state) => state.instruments?.success);
  const instrumentsError = useSelector((state) => state.instruments?.error);

  const routerPaths = getRouterPaths(type);
  const bulkDeleteButton = selectedRows && selectedRows.length > 1;

  const handleSearchChange = useCallback((value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setIsSearchActive(!!value);
      setSearchQuery(value);
    }, 500);
  }, []);
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const sensorActions = [
    {
      icon: Edit,
      tooltip: "Modifica",
      onClick: (rowId) => navigate(`${routerPaths.updateTool}/${rowId}`),
    },
    {
      icon: Delete,
      tooltip: "Elimina",
      onClick: (rowId) => {
        const instrumentObject = instruments.find(
          (instrument) => instrument.id === rowId,
        );
        setSelectedInstrument(instrumentObject);
        setOpenDialog(true);
      },
    },
  ];

  const handleDeleteSensor = () => {
    if (selectedInstrument.instrument_type === "gateway") {
      dispatch(
        deleteGatewayStart({
          gatewayId: selectedInstrument.id,
          accessToken,
        }),
      );
      setOpenDialog(false);
    } else {
      dispatch(
        deleteSensorStart({
          sensorId: selectedInstrument.id,
          accessToken,
        }),
      );
      setOpenDialog(false);
    }
  };

  const handleResetInstrumentsState = () => {
    dispatch(resetDeleteGatewayState());
    dispatch(resetDeleteSensorState());
    dispatch(resetInstrumentsStatus());
  };

  const handleRowSelectionModelChange = (newRowSelectionModel) => {
    setSelectedRows(newRowSelectionModel);
  };

  const handleBulkDelete = () => {
    setOpenBulkDeleteDialog(true);
  };

  const handleConfirmBulkDelete = () => {
    dispatch(
      bulkDeleteInstrumentsRequest({
        accessToken,
        instrumentsIds: selectedRows,
      }),
    );
    setOpenBulkDeleteDialog(false);
  };

  const handleCloseBulkDeleteDialog = () => {
    setOpenBulkDeleteDialog(false);
  };

  useEffect(() => {
    if (!accessToken) return;

    if (success || gatewaySuccess || bulkDeleteSuccess) {
      dispatch(fetchInstrumentsStart({ accessToken }));
      setSelectedInstrument(null);
      dispatch(resetInstrumentsStatus());
    }
  }, [dispatch, success, gatewaySuccess, accessToken, bulkDeleteSuccess]);

  useEffect(() => {
    if (!accessToken) return;

    dispatch(
      fetchInstrumentsStart({ accessToken, search: searchQuery }),
    );
    dispatch(fetchSensorsStart({ accessToken }));
  }, [accessToken, dispatch, searchQuery]);

  return (
    <Box width="100%" height="100%">
      {isLoading ? (
        <DataGridComponent
          rows={[]}
          columns={toolsTableColumns(sensorActions)}
          inputPlaceholder="Cerca nome o EUI"
          onSearchChange={handleSearchChange}
          buttonLabel={"Nuovo strumento"}
          showButton
          showBulkDeleteButton={bulkDeleteButton}
          buttonHref={"/tools/addTool"}
          loading={true}
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          checkboxSelection
        />
      ) : instruments?.length === 0 && !isSearchActive ? (
        <EmptyState
          text={"Non hai ancora configurato nessuno strumento"}
          buttonLabel={"Nuovo strumento"}
          href={"/tools/addTool"}
        />
      ) : (
        <DataGridComponent
          rows={instruments}
          columns={toolsTableColumns(sensorActions)}
          inputPlaceholder="Cerca nome o EUI"
          onSearchChange={handleSearchChange}
          buttonLabel={"Nuovo strumento"}
          showButton
          buttonHref={"/tools/addTool"}
          loading={isLoading}
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          checkboxSelection
          showBulkDeleteButton={bulkDeleteButton}
          onBulkDeleteClick={handleBulkDelete}
        />
      )}
      <CustomDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={dialogMessages.deleteTool.title}
        description={dialogMessages.deleteTool.description}
        onConfirm={handleDeleteSensor}
      />
      <CustomDialog
        open={openBulkDeleteDialog}
        onClose={handleCloseBulkDeleteDialog}
        title={dialogMessages.bulkDeleteInstruments.title}
        description={dialogMessages.bulkDeleteInstruments.description}
        onConfirm={handleConfirmBulkDelete}
      />
      {(success || gatewaySuccess) && (
        <CustomAlert
          severity="success"
          message={`${success ? "Sensore" : "Gateway"} eliminato con successo`}
          onClose={handleResetInstrumentsState}
        />
      )}
      {(sensorError || gatewayError || instrumentsError) && (
        <CustomAlert
          severity="error"
          message={getErrorMessage(
            sensorError || gatewayError || instrumentsError,
            {
              fallback: "Errore durante l'operazione sugli strumenti",
            },
          )}
          onClose={handleResetInstrumentsState}
        />
      )}
    </Box>
  );
};

export default ToolsPage;
